import { useSyncExternalStore } from 'react';
import { useAuth } from '@/lib/auth/AuthContext';

type Preferences = { followed: string[]; graphed: string[] };
const memory = new Map<string, string>();
const event = 'maternal-lab-preferences';
function read(key: string) {
  try {
    return localStorage.getItem(key) ?? '';
  } catch {
    return memory.get(key) ?? '';
  }
}
function parse(raw: string): Preferences {
  try {
    const value = JSON.parse(raw);
    const strings = (v: unknown): string[] =>
      Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
    return { followed: strings(value.followed), graphed: strings(value.graphed) };
  } catch {
    return { followed: [], graphed: [] };
  }
}
function subscribe(callback: () => void) {
  window.addEventListener(event, callback);
  window.addEventListener('storage', callback);
  return () => {
    window.removeEventListener(event, callback);
    window.removeEventListener('storage', callback);
  };
}
export function useLabPreferences() {
  const { user, organization } = useAuth();
  const key = `maternal-lab-preferences:${organization?.organization_id ?? ''}:${user?.id ?? ''}`;
  const raw = useSyncExternalStore(
    subscribe,
    () => read(key),
    () => '',
  );
  const preferences = parse(raw);
  function toggle(kind: keyof Preferences, row: string) {
    const current = parse(read(key));
    current[kind] = current[kind].includes(row) ? current[kind].filter((x) => x !== row) : [...current[kind], row];
    const next = JSON.stringify(current);
    memory.set(key, next);
    try {
      localStorage.setItem(key, next);
    } catch {
      /* Keep preferences for this session when storage is unavailable. */
    }
    window.dispatchEvent(new Event(event));
  }
  return { ...preferences, toggle };
}
